import { useEffect, useRef, useState } from 'react';
import { ImagePlus, Loader2, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import api from '../lib/api';
import './imageUploadField.css';
import { publicImageUrl } from '../lib/media';

export const ImageUploadField = ({ value, onChange, label, endpoint, fields, testId, disabled = false, onBusyChange, shape = 'logo' }) => {
  const input = useRef(null);
  const request = useRef(null);
  const busyCallback = useRef(onBusyChange);
  busyCallback.current = onBusyChange;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [value]);
  useEffect(() => () => { request.current?.abort(); busyCallback.current?.(false); }, []);
  const upload = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError('');
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) { setError('Choose a PNG, JPEG or WebP image.'); return; }
    if (!file.size || file.size > 5 * 1024 * 1024) { setError('Choose an image up to 5 MB.'); return; }
    const controller = new AbortController(); request.current = controller;
    setBusy(true); busyCallback.current?.(true);
    try {
      const body = new FormData(); body.append('file', file);
      Object.entries(fields || {}).forEach(([key, entry]) => { if (entry !== undefined && entry !== null && entry !== '') body.append(key, String(entry)); });
      const result = await api.post(endpoint, body, { headers: { 'Content-Type': 'multipart/form-data' }, signal: controller.signal });
      if (!controller.signal.aborted) onChange(result.data.url);
    } catch (e) {
      if (!controller.signal.aborted) setError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Could not upload this image. Your saved image is unchanged.');
    } finally { if (!controller.signal.aborted) { setBusy(false); busyCallback.current?.(false); } }
  };
  return <div className="image-upload-field" data-testid={`${testId}-field`}>
    <label className="text-sm font-medium" htmlFor={`${testId}-file`}>{label}</label>
    <div className="image-upload-content">
      <div className={`image-upload-preview image-upload-${shape}`} data-testid={`${testId}-preview`}>
        {value && !failed ? <img src={publicImageUrl(value)} alt={`${label} preview`} data-testid={`${testId}-image`} onError={() => setFailed(true)} /> : <ImagePlus size={30} className="text-slate-400" aria-hidden="true" />}
      </div>
      <div className="image-upload-actions">
        <input ref={input} id={`${testId}-file`} type="file" accept="image/png,image/jpeg,image/webp" disabled={busy || disabled} onChange={upload} className="sr-only" data-testid={`${testId}-file`} />
        <Button type="button" variant="outline" disabled={busy || disabled} onClick={() => input.current?.click()} data-testid={`${testId}-upload`}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImagePlus className="mr-2 h-4 w-4" />}{busy ? 'Uploading…' : value ? 'Replace image' : 'Upload image'}
        </Button>
        {value && <Button type="button" variant="ghost" disabled={busy || disabled} onClick={() => { onChange(''); setError(''); }} data-testid={`${testId}-remove`}><Trash2 className="mr-2 h-4 w-4" />Remove</Button>}
        <p className="text-xs text-muted-foreground" data-testid={`${testId}-requirements`}>PNG, JPEG or WebP · up to 5 MB</p>
        <p className="text-xs text-muted-foreground" data-testid={`${testId}-save-notice`}>Changes apply when you save.</p>
      </div>
    </div>
    {busy && <p role="status" className="text-xs text-muted-foreground" data-testid={`${testId}-progress`}>Uploading image…</p>}
    {failed && !error && <p role="status" className="text-xs text-amber-700" data-testid={`${testId}-preview-error`}>The saved image is unavailable. You can replace or remove it.</p>}
    {error && <p role="alert" className="text-sm text-red-700" data-testid={`${testId}-error`}>{error}</p>}
  </div>;
};